import { useState } from "react";
import { Icon } from "@/components/ui/Icon";

/**
 * Sozlama qiymati: maydon, matnga o'girish va tekshirish.
 *
 * Bitta joyda: qiymat ikki yerda tahrirlanadi — ro'yxatdagi
 * satrda va «yangi qiymat» oynasida. Ikki nusxa bo'lsa biri
 * `true` ni, ikkinchisi `"true"` ni yuborib, server xatosi
 * qaysi formadan kelganiga qarab farq qilardi.
 */

/** Sozlama ta'rifi (`/config-definitions`). */
export interface Definition {
  key: string;
  category: string;
  par_id?: string;
  owner_role?: string;
  value_type: ValueType;
  scope_kind: "GLOBAL_ONLY" | "WAREHOUSE";
  is_feature_flag: boolean;
  default_value: unknown;
  description_uz: string;
  description_ru: string;
  /** Oddiy tildagi izoh — yangi kelgan xodim uchun. */
  help_uz?: string;
}

export type ValueType =
  | "BOOLEAN"
  | "INTEGER"
  | "DECIMAL"
  | "STRING"
  | "DURATION"
  | "OBJECT"
  | "LIST";

/** Qiymat maydoni — tur bo'yicha. */
export function ValueField({
  definition,
  value,
  onChange,
  error,
  disabled,
}: {
  definition: Definition;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  /** Vakolat yo'q bo'lsa maydon KO'RINADI, lekin tahrirlanmaydi. */
  disabled?: boolean;
}) {
  if (definition.value_type === "BOOLEAN") {
    return (
      <div className="field">
        <span className="field-label">Qiymat</span>
        <div className="segmented" role="group">
          {["true", "false"].map((option) => (
            <button
              key={option}
              type="button"
              className={`segmented-item${value === option ? " active" : ""}`}
              disabled={disabled}
              onClick={() => onChange(option)}
            >
              {option === "true" ? "Yoqilgan" : "O'chirilgan"}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Obyekt va ro'yxat ham ODDIY maydonlar bo'lib ochiladi: xodim
  // qavs va qo'shtirnoqni to'g'ri qo'yishi shart emas, bitta
  // vergul unutilsa butun sozlama saqlanmay qolardi.
  if (definition.value_type === "OBJECT") {
    return <ObjectField definition={definition} value={value} onChange={onChange} error={error} disabled={disabled} />;
  }
  if (definition.value_type === "LIST") {
    return <ListField definition={definition} value={value} onChange={onChange} error={error} disabled={disabled} />;
  }

  return (
    <label className="field">
      <span className="field-label">Qiymat</span>
      <input
        className="input mono"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      <span className="field-hint">{HINTS[definition.value_type]}</span>
      {error && <span className="field-hint" style={{ color: "var(--error)" }}>{error}</span>}
    </label>
  );
}

/** JSON matnini xavfsiz o'qish — buzilgan bo'lsa `null`. */
function readJSON(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Yangi qiymatni ESKISINING turida yozadi.
 *
 * Miqdorlar katalogda MATN sifatida saqlanadi (`"0.80"`), chunki
 * songa o'tkazilsa aniqlik yo'qoladi. Maydonni tahrirlash shu
 * turni o'zgartirib yubormasligi kerak.
 */
function sameShape(previous: unknown, next: string): unknown {
  if (typeof previous === "number") {
    const parsed = Number(next);
    return next.trim() !== "" && Number.isFinite(parsed) ? parsed : next;
  }
  if (typeof previous === "boolean") return next === "true";
  return next;
}

/** Obyekt — har bir maydon alohida qatorda. */
function ObjectField({
  definition,
  value,
  onChange,
  error,
  disabled,
}: {
  definition: Definition;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}) {
  const parsed = readJSON(value);
  const current = (parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? (parsed as Record<string, unknown>)
    : null);
  const fallback = readJSON(JSON.stringify(definition.default_value ?? {}));
  const defaults = (fallback && typeof fallback === "object" && !Array.isArray(fallback)
    ? (fallback as Record<string, unknown>)
    : {});

  // Matn buzilgan bo'lsa (faqat qo'lda tahrirlashdan keyin)
  // xom JSON ga qaytamiz: kiritilgan matn YO'QOLMASIN.
  if (!current) {
    return (
      <label className="field">
        <span className="field-label">Qiymat</span>
        <textarea className="input mono" rows={4} value={value} disabled={disabled}
          onChange={(event) => onChange(event.target.value)} />
        <span className="field-hint" style={{ color: "var(--error)" }}>
          {error ?? "JSON formati buzilgan"}
        </span>
      </label>
    );
  }

  // Maydonlar ro'yxati: standart qiymat tartibi — u sozlamaning
  // «shakli». Keyin faqat qo'shimcha kalitlar.
  const fields = Object.keys(defaults).concat(
    Object.keys(current).filter((key) => !(key in defaults)),
  );
  // Standart bo'sh bo'lsa shakl ERKIN (masalan jarima shkalasi):
  // kalitni ham xodim yozadi.
  const freeForm = Object.keys(defaults).length === 0;

  const write = (next: Record<string, unknown>) => onChange(JSON.stringify(next, null, 2));

  return (
    <div className="field">
      <span className="field-label">Qiymat</span>
      <div className="grid-2">
        {fields.map((key) => (
          <label className="field" key={key}>
            <span className="field-label">{fieldLabel(definition.key, key)}</span>
            <div className="field-row">
              <input
                className="input mono"
                value={String(current[key] ?? defaults[key] ?? "")}
                disabled={disabled}
                onChange={(event) =>
                  write({ ...current, [key]: sameShape(current[key] ?? defaults[key], event.target.value) })
                }
              />
              {freeForm && (
                <button
                  type="button"
                  className="btn btn-icon"
                  disabled={disabled}
                  title="Qatorni o'chirish"
                  onClick={() => {
                    const next = { ...current };
                    delete next[key];
                    write(next);
                  }}
                >
                  <Icon name="close" size={16} />
                </button>
              )}
            </div>
          </label>
        ))}
      </div>
      {freeForm && (
        <NewEntryRow
          disabled={disabled}
          exists={(key) => key in current}
          onAdd={(key) => write({ ...current, [key]: "" })}
        />
      )}
      {freeForm && fields.length === 0 && (
        <span className="field-hint">Hozircha bitta ham qator yo'q.</span>
      )}
      {error && <span className="field-hint" style={{ color: "var(--error)" }}>{error}</span>}
    </div>
  );
}

/** Ro'yxat — har bir element alohida qatorda. */
function ListField({
  definition,
  value,
  onChange,
  error,
  disabled,
}: {
  definition: Definition;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}) {
  const parsed = readJSON(value);
  const items = Array.isArray(parsed) ? parsed : null;

  if (!items) {
    return (
      <label className="field">
        <span className="field-label">Qiymat</span>
        <textarea className="input mono" rows={4} value={value} disabled={disabled}
          onChange={(event) => onChange(event.target.value)} />
        <span className="field-hint" style={{ color: "var(--error)" }}>
          {error ?? "JSON formati buzilgan"}
        </span>
      </label>
    );
  }

  const write = (next: unknown[]) => onChange(JSON.stringify(next, null, 2));

  return (
    <div className="field">
      <span className="field-label">Qiymat</span>
      {items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="field-row" key={index}>
          <input
            className="input mono"
            value={String(item ?? "")}
            disabled={disabled}
            onChange={(event) => {
              const next = [...items];
              next[index] = sameShape(item, event.target.value);
              write(next);
            }}
          />
          <button
            type="button"
            className="btn btn-icon"
            disabled={disabled}
            title="Qatorni o'chirish"
            onClick={() => write(items.filter((_, position) => position !== index))}
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      ))}
      {items.length === 0 && <span className="field-hint">Ro'yxat bo'sh.</span>}
      <NewEntryRow
        disabled={disabled}
        label="Qo'shish"
        exists={(entry) => items.some((item) => String(item) === entry)}
        onAdd={(entry) => write([...items, entry])}
      />
      {error && <span className="field-hint" style={{ color: "var(--error)" }}>{error}</span>}
      {definition.key === "notification.channels" && (
        <span className="field-hint">Mumkin qiymatlar: IN_APP, TELEGRAM, EMAIL.</span>
      )}
    </div>
  );
}

/** «Qator qo'shish» — bo'sh qiymat va takror qo'shilmaydi. */
function NewEntryRow({
  onAdd,
  exists,
  disabled,
  label = "Qator qo'shish",
}: {
  onAdd: (entry: string) => void;
  exists: (entry: string) => boolean;
  disabled?: boolean;
  label?: string;
}) {
  const [entry, setEntry] = useState("");
  const clean = entry.trim();
  return (
    <div className="field-row">
      <input
        className="input mono"
        value={entry}
        disabled={disabled}
        placeholder={label}
        onChange={(event) => setEntry(event.target.value)}
      />
      <button
        type="button"
        className="btn btn-tonal"
        disabled={disabled || clean === "" || exists(clean)}
        onClick={() => {
          onAdd(clean);
          setEntry("");
        }}
      >
        <Icon name="plus" size={16} />
      </button>
    </div>
  );
}

/**
 * Obyekt maydonlarining o'zbekcha nomlari.
 *
 * Kalit sozlama bilan birga yoziladi: `a` yoki `x` o'z-o'zidan
 * hech nima anglatmaydi va ikki sozlamada ikki xil ma'noda
 * ishlatiladi.
 */
const FIELD_LABELS: Record<string, string> = {
  "replenishment.ox_default_min_max.min_stock": "Eng kam qoldiq (shundan tushsa to'ldiriladi)",
  "replenishment.ox_default_min_max.max_stock": "Eng ko'p qoldiq (shungacha to'ldiriladi)",
  "slotting.abc_thresholds.a": "A toifasi chegarasi",
  "slotting.abc_thresholds.b": "B toifasi chegarasi",
  "slotting.xyz_thresholds.x": "X toifasi chegarasi",
  "slotting.xyz_thresholds.y": "Y toifasi chegarasi",
  "picking.pallet_plan.width_cm": "Pallet eni, sm",
  "picking.pallet_plan.length_cm": "Pallet uzunligi, sm",
  "picking.pallet_plan.max_height_cm": "Eng baland yuk, sm (taglik bilan)",
  "picking.pallet_plan.base_height_cm": "Taglik balandligi, sm",
  "picking.pallet_plan.max_weight_kg": "Eng og'ir yuk, kg",
};

function fieldLabel(settingKey: string, field: string): string {
  return FIELD_LABELS[`${settingKey}.${field}`] ?? FIELD_LABELS[field] ?? field;
}

const HINTS: Record<ValueType, string> = {
  BOOLEAN: "",
  INTEGER: "Butun son, masalan 60.",
  // Miqdorlar MATN sifatida yuboriladi (dev_spec.md §7.2): son
  // turiga o'tkazilsa yaxlitlash xatosi kirardi.
  DECIMAL: "O'nlik son, matn sifatida: \"1.5\".",
  STRING: "Matn.",
  DURATION: "Davomiylik: 30s, 15m, 2h.",
  OBJECT: "JSON obyekt: { \"min_stock\": \"20\" }",
  LIST: "JSON ro'yxat: [\"IN_APP\", \"TELEGRAM\"]",
};

/**
 * Qiymatni TAHRIRLANADIGAN matnga o'giradi.
 *
 * Obyekt va ro'yxat chiroyli JSON bo'lib ochiladi: bir qatorga
 * siqilgan `{"a":"0.80","b":"0.95"}` ni maydonda tuzatish
 * og'ir edi.
 */
export function valueText(type: ValueType, value: unknown): string {
  if (type === "OBJECT" || type === "LIST") {
    return JSON.stringify(value ?? (type === "LIST" ? [] : {}), null, 2);
  }
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return String(value);
}

export function defaultText(definition: Definition): string {
  return valueText(definition.value_type, definition.default_value);
}

export type Parsed = { ok: true; value: unknown } | { ok: false; error?: string };

/**
 * Matnni sozlama turiga mos qiymatga o'giradi.
 *
 * DECIMAL matn bo'lib QOLADI: `Number()` ga o'tkazilsa aniqlik
 * yo'qolardi (dev_spec.md §7.2). INTEGER esa haqiqiy son —
 * katalogda u shunday e'lon qilingan.
 */
export function parseValue(definition: Definition | undefined, raw: string): Parsed {
  if (!definition) return { ok: false };
  const trimmed = raw.trim();

  switch (definition.value_type) {
    case "BOOLEAN":
      if (trimmed !== "true" && trimmed !== "false") return { ok: false, error: "true yoki false" };
      return { ok: true, value: trimmed === "true" };

    case "INTEGER": {
      if (!/^-?\d+$/.test(trimmed)) return { ok: false, error: "butun son bo'lishi kerak" };
      return { ok: true, value: Number(trimmed) };
    }

    case "DECIMAL":
      if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return { ok: false, error: "son bo'lishi kerak" };
      return { ok: true, value: trimmed };

    case "DURATION":
      if (!/^\d+(\.\d+)?(ns|us|ms|s|m|h)$/.test(trimmed)) {
        return { ok: false, error: "masalan 30s, 15m, 2h" };
      }
      return { ok: true, value: trimmed };

    case "OBJECT":
    case "LIST":
      try {
        const parsed = JSON.parse(trimmed);
        const isArray = Array.isArray(parsed);
        if (definition.value_type === "LIST" && !isArray) {
          return { ok: false, error: "ro'yxat bo'lishi kerak" };
        }
        if (definition.value_type === "OBJECT" && (isArray || typeof parsed !== "object" || parsed === null)) {
          return { ok: false, error: "obyekt bo'lishi kerak" };
        }
        return { ok: true, value: parsed };
      } catch {
        return { ok: false, error: "JSON formati buzilgan" };
      }

    default:
      if (!trimmed) return { ok: false, error: "bo'sh bo'lmasligi kerak" };
      return { ok: true, value: trimmed };
  }
}
